/**
 * Plain-coordinate geometry for the state legislative district pages (sites#471): how much of one district falls in
 * each district of another layer (nesting: "Within State Senate District 1"), and which districts of the same layer
 * border it. Inputs are the simplified per-state TIGER GeoJSON features (/geo/sld{u,l}/us-sld{u,l}-<st>.geojson), in
 * lon/lat degrees. Overlap is estimated by point-sampling, so shares are approximate (a few percent); nothing here
 * needs a geometry library.
 */

export type Ring = number[][];
export type PolygonRings = Ring[];  // outer ring then holes

export interface Shape {
  key: string;
  polygons: PolygonRings[];
  bbox: [number, number, number, number];  // minLon, minLat, maxLon, maxLat
}

/** A GeoJSON Polygon / MultiPolygon geometry as polygons of rings, with its bounding box. */
export function toShape(key: string, geometry: { type: string; coordinates: any } | null | undefined): Shape | null {
  if (!geometry) return null;
  let polygons: PolygonRings[] = geometry.type === 'Polygon' ? [geometry.coordinates]
    : geometry.type === 'MultiPolygon' ? geometry.coordinates : [];
  if (!polygons.length) return null;
  // Alaska's Aleutians cross the antimeridian (172°E..180°). US state legislative districts have no other east
  // longitudes, so every east longitude is brought west: boxes stay tight and all shapes share one frame.
  if (polygons.some(poly => (poly[0] || []).some(c => c[0] > 0))) {
    polygons = polygons.map(poly => poly.map(ring => ring.map(([x, y]) => [x > 0 ? x - 360 : x, y])));
  }
  let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity;
  for (const poly of polygons) for (const [lon, lat] of poly[0] || []) {
    if (lon < minLon) minLon = lon; if (lon > maxLon) maxLon = lon;
    if (lat < minLat) minLat = lat; if (lat > maxLat) maxLat = lat;
  }
  if (!Number.isFinite(minLon)) return null;
  return { key, polygons, bbox: [minLon, minLat, maxLon, maxLat] };
}

function inRing(lon: number, lat: number, ring: Ring): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j];
    if ((yi > lat) !== (yj > lat) && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Whether a point is inside a shape (even-odd within each polygon: inside the outer ring and no hole). */
export function contains(shape: Shape, lon: number, lat: number): boolean {
  const [a, b, c, d] = shape.bbox;
  if (lon < a || lon > c || lat < b || lat > d) return false;
  for (const poly of shape.polygons) {
    if (!poly.length || !inRing(lon, lat, poly[0])) continue;
    if (!poly.slice(1).some(hole => inRing(lon, lat, hole))) return true;
  }
  return false;
}

/** Up to about `target` points evenly spread inside a shape (a grid over its box, finer until enough land inside). */
export function samplePoints(shape: Shape, target = 200): Array<[number, number]> {
  const [a, b, c, d] = shape.bbox;
  let points: Array<[number, number]> = [];
  for (let n = Math.ceil(Math.sqrt(target)); n <= 160; n *= 2) {
    points = [];
    for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) {
      const lon = a + ((i + 0.5) / n) * (c - a), lat = b + ((j + 0.5) / n) * (d - b);
      if (contains(shape, lon, lat)) points.push([lon, lat]);
    }
    if (points.length >= target / 2) break;
  }
  return points;
}

const boxesTouch = (p: Shape['bbox'], q: Shape['bbox'], pad = 0) =>
  p[0] - pad <= q[2] && q[0] - pad <= p[2] && p[1] - pad <= q[3] && q[1] - pad <= p[3];

export interface Overlap { key: string; share: number }

/** The share of `shape` (by sampled points) inside each of `others`, largest first, dropping shares under `min`. */
export function overlaps(shape: Shape, others: Shape[], min = 0.02, target = 200): Overlap[] {
  const candidates = others.filter(o => boxesTouch(shape.bbox, o.bbox));
  const points = samplePoints(shape, target);
  if (!points.length || !candidates.length) return [];
  const counts = new Map<string, number>();
  for (const [lon, lat] of points) {
    const hit = candidates.find(o => contains(o, lon, lat));
    if (hit) counts.set(hit.key, (counts.get(hit.key) || 0) + 1);
  }
  return [...counts].map(([key, n]) => ({ key, share: n / points.length }))
    .filter(o => o.share >= min).sort((x, y) => y.share - x.share);
}

/** "Within" when one district holds at least this share. */
export const NESTED_SHARE = 0.95;

/**
 * Districts of the same layer that share a boundary with `shape`. Each district is simplified on its own (0.002°
 * tolerance in GeoShapeImageSync), so neighbours' outlines do not coincide: walk this shape's outline, probe a little
 * outside it (`reach` degrees, about 450 m, in eight directions) and count the boundary points whose probes land in
 * another district. At least `minHits` boundary points must reach it, so a single corner touch does not count.
 */
export function neighbours(shape: Shape, others: Shape[], reach = 0.004, minHits = 2, samples = 400): string[] {
  const candidates = others.filter(o => o.key !== shape.key && boxesTouch(shape.bbox, o.bbox, reach));
  if (!candidates.length) return [];
  const rings = shape.polygons.map(poly => poly[0] || []).filter(r => r.length > 1);
  let perimeter = 0;
  for (const ring of rings) for (let i = 1; i < ring.length; i++) perimeter += Math.hypot(ring[i][0] - ring[i - 1][0], ring[i][1] - ring[i - 1][1]);
  if (!perimeter) return [];
  const step = Math.max(perimeter / samples, reach / 2);
  const directions = Array.from({ length: 8 }, (_, k) => [Math.cos((k * Math.PI) / 4) * reach, Math.sin((k * Math.PI) / 4) * reach]);
  const hits = new Map<string, number>();
  for (const ring of rings) {
    let next = 0, travelled = 0;
    for (let i = 1; i < ring.length; i++) {
      const [ax, ay] = ring[i - 1], [bx, by] = ring[i];
      const len = Math.hypot(bx - ax, by - ay);
      for (; next <= travelled + len; next += step) {
        const f = len ? (next - travelled) / len : 0;
        const x = ax + (bx - ax) * f, y = ay + (by - ay) * f;
        const reached = new Set<string>();
        for (const [dx, dy] of directions) {
          const px = x + dx, py = y + dy;
          if (contains(shape, px, py)) continue;
          const hit = candidates.find(o => contains(o, px, py));
          if (hit) reached.add(hit.key);
        }
        for (const key of reached) hits.set(key, (hits.get(key) || 0) + 1);
      }
      travelled += len;
    }
  }
  return [...hits].filter(([, n]) => n >= minHits).map(([key]) => key);
}

/** Census codes in chamber order: numeric codes by number, then the rest alphabetically (00A..00T, F-3, D06). */
export function compareCodes(a: string, b: string): number {
  const na = /^\d+$/.test(a), nb = /^\d+$/.test(b);
  if (na && nb) return Number(a) - Number(b);
  if (na !== nb) return na ? -1 : 1;
  return a.localeCompare(b, 'en', { numeric: true });
}
